// "Wat kost een run?" (W6 ronde 4; kostenmandaat: onder de go-grens — nu $25 in
// config/kosten.yaml — mag een run direct door, daarboven eerst owner-akkoord).
//
// De server raamt (POST /api/beheer/raming): hij kent de eerdere runs van de
// laan, de prijs van het model en de go-grens. Dit formulier stuurt alleen de
// laan en het aantal documenten en toont zijn zin en zijn oordeel. Geen eigen
// rekensom, en geen bedrag in de uitleg dat de server niet stuurde: de
// go-grens komt pas op het scherm als hij met de raming terugkomt.
//
// De controle op het aantal (heel getal, 1 tot 100.000) is die van het contract
// (kostenRamingRequest). Hij houdt alleen een zinloze aanroep tegen; de server
// keurt zelf ook, en zijn zin bij een 422 staat er dan letterlijk. Een laan die
// niet in de prijstabel staat (of geen bruikbare runs heeft) is geen fout: de
// server antwoordt dan met oordeel "onbekend" en een zin waarom.
//
// Een antwoord hoort bij de vraag die het kreeg. Staat er intussen iets anders
// in de velden, dan verdwijnt het — anders lees je de raming van laan A bij
// laan B. Tijdens het vragen staan de velden daarom dicht, en een laat
// antwoord dat toch niet meer past, komt niet op het scherm.

import { useId, useState, type FormEvent } from "react";

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { api } from "../../lib/api";
import type { KostenRamingRequest, KostenRamingResponse } from "../../contracts";
import { color, size, weight } from "../../tokens";
import { Blok, noot } from "./BronnenStukken";
import { DollarIcoon } from "./Iconen";
import { invoer, knopDonker } from "./stijl";
import { leesbaar } from "./woorden";

type Oordeel = KostenRamingResponse["oordeel"];

// Alleen het korte woord: de uitleg (bedrag, go-grens, waarom geen raming)
// staat in de zin van de server ernaast — die bouwt dit scherm niet na.
const OORDEEL: Record<Oordeel, { woord: string; tone: "blue" | "amber" | "neutral" }> = {
  go: { woord: "mag door", tone: "blue" },
  owner_akkoord: { woord: "eerst owner-akkoord", tone: "amber" },
  onbekend: { woord: "geen raming", tone: "neutral" },
};

/** "100", "1000" of "1.000" → het getal; al het andere, of buiten 1 tot
 *  100.000, → null. Een punt als duizendtal omdat men dat zo typt. */
export function leesAantal(tekst: string): number | null {
  const t = tekst.trim();
  if (!/^\d+$/.test(t) && !/^\d{1,3}(\.\d{3})+$/.test(t)) return null;
  const n = Number(t.replace(/\./g, ""));
  return Number.isInteger(n) && n >= 1 && n <= 100_000 ? n : null;
}

type Staat =
  | { soort: "rust" }
  | { soort: "bezig" }
  // Het antwoord draagt zijn eigen laan en aantal (de server herhaalt ze).
  | { soort: "klaar"; data: KostenRamingResponse }
  // Een fout niet: daarom bewaren we de vraag erbij.
  | { soort: "fout"; reden: string; vraag: KostenRamingRequest };

/** Een laan in de keuzelijst: de sleutel gaat naar de server, het woord van de
 *  server (`laanLabel` uit `maanden[].lanen`) is wat je leest. */
export type LaanKeuze = { laan: string; laanLabel: string };

export function Raming({ lanen }: { lanen: readonly LaanKeuze[] }) {
  const [laan, setLaan] = useState(lanen[0]?.laan ?? "");
  const [tekst, setTekst] = useState("100");
  const [staat, setStaat] = useState<Staat>({ soort: "rust" });
  const id = useId();

  const aantal = leesAantal(tekst);
  const bezig = staat.soort === "bezig";
  const kan = !bezig && aantal !== null && laan !== "";

  // Een oud antwoord hoort bij de oude invoer: bij een wijziging gaat het weg.
  const wijzig = (zet: () => void) => {
    zet();
    if (staat.soort === "klaar" || staat.soort === "fout") setStaat({ soort: "rust" });
  };

  // Tweede slot, voor een antwoord dat pas binnenkomt ná een wijziging: alleen
  // wat bij de huidige velden hoort, komt op het scherm.
  const hoortBij = (v: KostenRamingRequest) => v.laan === laan && v.aantal === aantal;
  const uitkomst = staat.soort === "klaar" && hoortBij(staat.data) ? staat.data : null;
  const fout = staat.soort === "fout" && hoortBij(staat.vraag) ? staat.reden : null;

  async function raam(e: FormEvent) {
    e.preventDefault();
    if (!kan || aantal === null) return;
    const vraag = { laan, aantal };
    setStaat({ soort: "bezig" });
    try {
      setStaat({ soort: "klaar", data: await api.raming(vraag) });
    } catch (err) {
      setStaat({
        soort: "fout",
        reden: leesbaar(err, "De raming lukte niet — probeer het zo nog eens."),
        vraag,
      });
    }
  }

  return (
    <Blok titel="Wat kost een run?">
      <Card style={{ padding: "16px 18px" }}>
        {lanen.length === 0 ? (
          <EmptyReason reason="er is nog geen laan met runs om een raming op te baseren" />
        ) : (
          <>
            <form
              onSubmit={(e) => void raam(e)}
              style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}
            >
              <div style={veld}>
                <label htmlFor={`${id}-laan`} style={veldLabel}>
                  Laan
                </label>
                <select
                  id={`${id}-laan`}
                  value={laan}
                  disabled={bezig}
                  onChange={(e) => wijzig(() => setLaan(e.target.value))}
                  style={{ ...invoer, width: "auto", minWidth: 160 }}
                >
                  {lanen.map((l) => (
                    <option key={l.laan} value={l.laan}>
                      {l.laanLabel}
                    </option>
                  ))}
                </select>
              </div>
              <div style={veld}>
                <label htmlFor={`${id}-aantal`} style={veldLabel}>
                  Aantal documenten
                </label>
                <input
                  id={`${id}-aantal`}
                  type="text"
                  inputMode="numeric"
                  value={tekst}
                  disabled={bezig}
                  onChange={(e) => wijzig(() => setTekst(e.target.value))}
                  aria-invalid={aantal === null}
                  aria-describedby={`${id}-hint`}
                  style={{ ...invoer, width: 140 }}
                />
              </div>
              <button
                type="submit"
                disabled={!kan}
                style={{ ...knopDonker(!kan), display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <DollarIcoon /> {bezig ? "bezig…" : "Raam de kosten"}
              </button>
            </form>
            <p
              id={`${id}-hint`}
              style={{ ...noot, color: aantal === null ? color.accentRed : color.faint }}
            >
              {/* Niet alleen rood: de zin zelf zegt wat er moet gebeuren. */}
              {aantal === null
                ? "Vul een heel getal van 1 tot 100.000 in."
                : "Een heel getal van 1 tot 100.000."}
            </p>

            {/* Een levend gebied dat er altijd staat: een schermlezer meldt dan
                zowel "bezig" als het antwoord zodra het verschijnt (een gebied
                dat pas mét zijn inhoud verschijnt, wordt vaak niet voorgelezen). */}
            <div role="status" aria-live="polite" data-raming-status>
              {bezig && (
                <p style={{ ...noot, marginTop: 12 }}>De raming wordt opgevraagd…</p>
              )}
              {uitkomst && (
                <div
                  data-raming
                  style={{
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: `1px solid ${color.lineFaint}`,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    gap: 10,
                  }}
                >
                  <ReasonBadge
                    text={OORDEEL[uitkomst.oordeel].woord}
                    tone={OORDEEL[uitkomst.oordeel].tone}
                  />
                  <span
                    style={{
                      fontSize: size.body,
                      color: color.ink,
                      lineHeight: 1.5,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {uitkomst.tekst}
                  </span>
                </div>
              )}
            </div>
            {fout && (
              <p role="alert" style={{ marginTop: 12, fontSize: size.meta, color: color.accentRed }}>
                {fout}
              </p>
            )}

            <p style={noot}>
              De raming komt van de server: het gemiddelde van eerdere runs van deze laan, tegen de
              prijs van vandaag — een bovengrens. Onder de go-grens mag een run direct door; vanaf
              de go-grens eerst owner-akkoord.
            </p>
          </>
        )}
      </Card>
    </Blok>
  );
}

const veld = { display: "flex", flexDirection: "column", gap: 4 } as const;
const veldLabel = { fontSize: 12, fontWeight: weight.medium, color: color.soft } as const;
